/** Unique widget ID. If no argument is provided, the result of the first rendered widget is returned. */
export type WidgetId = number;

export enum SmartCaptchaSubscribeEvent {
    ChallengeVisible = 'challenge-visible',
    ChallengeHidden = 'challenge-hidden',
    NetworkError = 'network-error',
    Success = 'success',
    TokenExpired = 'token-expired',
}

export interface SmartCaptchaRenderOptions {
    /** Указан в environment > yandexCaptchaPublicKey */
    sitekey: string;
    /** Handler function. */
    callback?: (token: string) => void;
    /** Widget language */
    hl?: 'ru' | 'en' | 'be' | 'kk' | 'tt' | 'uk' | 'uz' | 'tr';
    /** Launching CAPTCHA in test mode. The user will always get a task. Use this property for debugging and testing only. */
    test?: boolean;
    /** Launching CAPTCHA in WebView. It is used to make user response validation more precise when adding CAPTCHA to mobile apps via WebView. */
    webview?: boolean;
    /** Invisible CAPTCHA is a way of connecting the {{ captcha-name }} widget without the "I’m not a robot" button on the page.
     *  Only users whose requests are considered suspicious by {{ captcha-name }} will see the task pop-up window. */
    invisible?: boolean;
    /** Position of the data processing notice section. */
    shieldPosition?: `top-left` | `center-left` | `bottom-left` | `top-right` | `center-right` | `bottom-right`;
    /** Hide the data processing notice section. */
    hideShield?: boolean;
}

export interface SmartCaptcha {
    /** The destroy method deletes widgets and any listeners they create. */
    destroy: (widgetId: WidgetId | undefined) => void;
    /** The execute method starts user validation. It is used to initiate the invisible CAPTCHA test at a certain event, e.g.,
     *  when the user clicks the Submit button of an authentication form. */
    execute: (widgetId: WidgetId | undefined) => void;
    /** The getResponse method returns the current value of the user token. */
    getResponse: (widgetId: WidgetId | undefined) => string;
    /** The render method is used to draw the widget. */
    render: <T = HTMLElement>(container: T | string, params: SmartCaptchaRenderOptions) => WidgetId;
    /** The reset method resets the widget to the initial state. */
    reset: (widgetId: WidgetId | undefined) => void;
    /** The subscribe method enables you to subscribe listeners to certain widget events.
     *  For instance, you can listen for the opening and closing of a task popup window.
     *  This may be useful for displaying the keyboard on mobile devices. */
    subscribe: (widgetId: WidgetId, event: SmartCaptchaSubscribeEvent, callback: Function) => () => void;
}

export interface SmartCaptchaResponse {
    status: 'ok' | 'failed';
    host?: string;
    message: string;
}
